import type { ReactElement } from 'react';

export interface FlowStep {
  /** 見出し（「対象者を決める」など、動詞で終える） */
  label: string;
  /** その手順で何が起きるかの一言 */
  detail: string;
}

/**
 * 登録・設定画面の冒頭に置く「全体の流れ」（D-108）。
 *
 * 時間を空けてから画面を開くと「ここで何をすればよいか」を思い出せない、という
 * 事務局からの指摘への対応。いま何番目にいて、この後に何が残るのかを最初に見せる。
 * 表示だけの部品で、遷移は持たない（押せそうに見えると誤操作を招くため）。
 */
export function StepFlow({
  title = '全体の流れ',
  steps,
  currentIndex,
  note,
}: {
  title?: string;
  steps: FlowStep[];
  /** いまいる手順（0始まり） */
  currentIndex: number;
  /** 流れの下に出す補足（「公開するまで受講生には見えません」など） */
  note?: string;
}): ReactElement {
  return (
    <section className="mb-5 rounded-sm border border-wpborder bg-wpcanvas px-5 py-4">
      <h2 className="mb-2 text-wppanel font-semibold text-wptext-strong">{title}</h2>
      <ol className="flex flex-col gap-2">
        {steps.map((step, index) => {
          const done = index < currentIndex;
          const current = index === currentIndex;
          return (
            <li key={step.label} className="flex flex-wrap items-baseline gap-2">
              <span
                aria-hidden="true"
                className={`flex h-5 w-5 shrink-0 translate-y-0.5 items-center justify-center rounded-full text-xs font-semibold ${
                  done
                    ? 'bg-wpnotice-success text-white'
                    : current
                      ? 'bg-wpaccent text-white'
                      : 'bg-wpborder text-wptext-strong'
                }`}
              >
                {done ? '✓' : index + 1}
              </span>
              <span
                className={
                  current ? 'font-semibold text-wptext-strong' : 'text-wptext-muted'
                }
              >
                {step.label}
              </span>
              {current && (
                <span className="rounded-sm bg-wpaccent px-2 py-0.5 text-xs font-semibold text-white">
                  いまここ
                </span>
              )}
              <span className="text-wptext-muted">— {step.detail}</span>
            </li>
          );
        })}
      </ol>
      {note && <p className="mt-2 text-wptext-muted">{note}</p>}
    </section>
  );
}
